// TODO: cleanup with research_assistant_gen_3 ff
//
// Extracted from ProjectChatPanel.tsx's ActiveChatBody (OR-6455) so
// ReadOnlyChatPanel can wire the SAME asset-card → detail-page navigation
// as the live chat panel, without duplicating (and risking drift from) the
// routing logic. Every callback here is pure navigation — none of them
// mutate anything — so reusing them for a read-only viewer is safe: OR-6455
// only needs to preview an owned or foreign asset, never act on it.

import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  getProjectCohortVariableViewPath,
  getProjectConceptGroupPath,
  getProjectVariablePath,
} from '@verantos-dev/core-constants';
import type { EpiArtifact } from '@verantos-dev/feature-epi-chat';

import { parseProjectPathSegments } from './parseProjectPathSegments';
import { useChatPreservingNavigate } from './useChatPreservingNavigate';

// OR-6115: distinguishes a library AV's UUID (curated/"Saved" record) from a
// bare knowledge slug in the AV-link click handler. A UUID routes through to
// the library detail unchanged; a slug gets the ``av.`` prefix.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ChatResourceNavigation {
  /** Also passed as ``EpiChatPanelLayout``'s ``onResourceNavigate`` —
   * ``resource_ref`` cards route BE-computed paths straight through it. */
  navigate: (route: string) => void;
  onArtifactOpen: (artifact: EpiArtifact) => void;
  onAvSlugClick: (idOrSlug: string) => void;
  onConceptGroupNavigate: (conceptGroupId: string) => void;
}

/**
 * Builds the click-to-preview navigation handlers chat asset cards use —
 * artifact cards, inline AV-slug links, resource_ref cards, and
 * concept-group links — scoped to ``projectId`` (and, when present, the
 * cohort the URL is currently nested under).
 */
export const useChatResourceNavigation = (projectId: string): ChatResourceNavigation => {
  const navigate = useChatPreservingNavigate();
  const { pathname } = useLocation();

  // Cohort context for variable routing. When the chat is open over a cohort
  // surface (``/projects/<pid>/cohort/<cid>/...``) variable clicks nest the
  // view under that cohort so the URL itself carries the "from inside cohort
  // X" context and the breadcrumb renders ``Projects / <project> / <cohort> /
  // <variable>`` — matching ``CriterionCard``'s "View full" behaviour.
  const cohortId = parseProjectPathSegments(pathname, projectId).cohortId;

  // Cohort-scoped when a cohort id is in the URL; flat project view otherwise.
  const variablePathFor = useCallback(
    (knowledgeId: string) =>
      cohortId
        ? getProjectCohortVariableViewPath(projectId, cohortId, knowledgeId)
        : getProjectVariablePath(projectId, knowledgeId),
    [cohortId, projectId]
  );

  const onArtifactOpen = useCallback(
    (artifact: EpiArtifact) => {
      switch (artifact.kind) {
        case 'av':
        case 'av_denovo':
        case 'composition': {
          const slug = artifact.name ?? artifact.id;
          const knowledgeId = slug.startsWith('av.') ? slug : `av.${slug}`;
          navigate(variablePathFor(knowledgeId));
          return;
        }
        case 'concept_group':
          navigate(getProjectConceptGroupPath(projectId, artifact.id));
          return;
        default:
          return;
      }
    },
    [navigate, projectId, variablePathFor]
  );

  // OR-6115: inline AV links route to the variable detail route via the same
  // chat-preserving navigate as the artifact cards.
  const onAvSlugClick = useCallback(
    (idOrSlug: string) => {
      const knowledgeId =
        idOrSlug.startsWith('av.') || UUID_PATTERN.test(idOrSlug) ? idOrSlug : `av.${idOrSlug}`;
      navigate(variablePathFor(knowledgeId));
    },
    [navigate, variablePathFor]
  );

  const onConceptGroupNavigate = useCallback(
    (conceptGroupId: string) => navigate(getProjectConceptGroupPath(projectId, conceptGroupId)),
    [navigate, projectId]
  );

  return useMemo(
    () => ({ navigate, onArtifactOpen, onAvSlugClick, onConceptGroupNavigate }),
    [navigate, onArtifactOpen, onAvSlugClick, onConceptGroupNavigate]
  );
};
